import { createSelector } from '@ngrx/store';
import {
  convertPadModeToPadEnforcementRule,
  CreateTwikeyInviteRequestV1,
  PadEnforcementRule,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  selectPadTwikeyRedirectToken,
  selectPayorBankAccountDetail,
  selectPayorDetail,
  selectPreAuthorizedDebitMode,
  selectSessionId,
  selectShouldRedirectToTwikeyForPadSignature,
  selectVirtualOtherwisePrimaryTenantId,
} from '@arc/unified-payments-frontend/shared-stores';
import {
  selectPayOnAccountState,
  selectSelectedPaymentAccount,
} from '../reducers';
import { selectLanguageInPadServiceFormat } from '@arc/unified-payments-frontend/i18n';
import {
  selectAmount,
  selectHasRemittanceFileToUpload,
} from './pay-on-account.selectors';
import { selectExternalUserUrn } from './pay-on-account-preview.selectors';

export const selectCreateTwikeyInviteRequestForUpdatingPadVerifiedStatus =
  createSelector(
    selectVirtualOtherwisePrimaryTenantId,
    selectSelectedPaymentAccount,
    selectSessionId,
    selectExternalUserUrn,
    selectPadTwikeyRedirectToken,
    selectAmount,
    (
      tenantId,
      selectedPaymentAccount,
      paymentEngineSessionId,
      externalUserUrn,
      twikeyToken,
      totalAmount,
    ) => {
      const payload: CreateTwikeyInviteRequestV1 = {
        paymentAccountBurn:
          selectedPaymentAccount?.paymentAccountIdentifierBurn,
        paymentEngineSessionId,
        externalUserUrn,
        twikeyToken,
        isOneTimePad: selectedPaymentAccount?.isOneTimePaymentAccount ?? false,
        padEnforcement: {
          rule: PadEnforcementRule.Optional,
          isEnforced: false,
        },
      };

      if (payload.isOneTimePad) {
        payload.oneTimeDebitAmount = totalAmount;
      }

      return {
        tenantId,
        payload,
      };
    },
  );

export const selectCreateTwikeyInviteRequestForRedirect = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectSelectedPaymentAccount,
  selectSessionId,
  selectExternalUserUrn,
  selectPadTwikeyRedirectToken,
  selectLanguageInPadServiceFormat,
  selectPreAuthorizedDebitMode,
  selectPayorDetail,
  selectPayorBankAccountDetail,
  selectAmount,
  (
    tenantId,
    selectedPaymentAccount,
    paymentEngineSessionId,
    externalUserUrn,
    twikeyToken,
    languageCode,
    padMode,
    payorDetail,
    payorBankAccountDetail,
    totalAmount,
  ) => {
    const payload: CreateTwikeyInviteRequestV1 = {
      paymentAccountBurn: selectedPaymentAccount?.paymentAccountIdentifierBurn,
      paymentEngineSessionId,
      externalUserUrn,
      twikeyToken,
      isOneTimePad: selectedPaymentAccount?.isOneTimePaymentAccount ?? false,
      languageCode,
      padEnforcement: {
        rule: convertPadModeToPadEnforcementRule(padMode),
        isEnforced: true,
      },
      payorDetail,
      payorBankAccountDetail,
    };

    if (payload.isOneTimePad) {
      payload.oneTimeDebitAmount = totalAmount;
    }

    return {
      tenantId,
      payload,
    };
  },
);

export const selectPadAgreementRemittanceFile = createSelector(
  selectPayOnAccountState,
  (state) => state.padAgreementRemittanceFile,
);

export const selectRemittanceFileUploadedForRedirect = createSelector(
  selectPayOnAccountState,
  (state) => state.padAgreementRemittanceFile != null,
);

export const selectShouldUploadRemittanceFileForRedirect = createSelector(
  selectShouldRedirectToTwikeyForPadSignature,
  selectHasRemittanceFileToUpload,
  selectRemittanceFileUploadedForRedirect,
  (
    shouldRedirectToTwikey,
    hasRemittanceFileToUpload,
    remittanceFileUploadedForRedirect,
  ) => {
    return (
      shouldRedirectToTwikey &&
      hasRemittanceFileToUpload &&
      !remittanceFileUploadedForRedirect
    );
  },
);

export const selectShouldSaveStateForRedirect = createSelector(
  selectShouldRedirectToTwikeyForPadSignature,
  selectHasRemittanceFileToUpload,
  selectRemittanceFileUploadedForRedirect,
  (
    shouldRedirectToTwikey,
    hasRemittanceFileToUpload,
    remittanceFileUploadedForRedirect,
  ) =>
    shouldRedirectToTwikey &&
    (!hasRemittanceFileToUpload || remittanceFileUploadedForRedirect),
);
